import { newUploadId, runGuardedEffect, type DurableLeaseToken, type DurableOwnershipPort, type OwnershipGate } from "@uploads";
import api from "@/api";
import {
	deleteFileUploadSession,
	cleanupCompletedFileUploadSession,
	getFileUploadSessionById,
	handoffFileUploadSessionToCancellation,
	updateFileUploadSession,
	updateFileUploadSessionStatus,
	type FileUploadSession
} from "@/db/fileUploadDb";
import { getAdminDurableOwnershipForCoordinator } from "./adminUploadLease";
import { initiateAdminPersistedTarget } from "./adminUploadTargetInitiator";
import { isUploadsUploadTarget } from "@uploads";
import { isOwnPendingTargetPlaceholder } from "./pendingUploadTarget";
import { uploadFailureLogDetails } from "./uploadLogRedaction";

export type AdminUploadCancellationResult = "acknowledged" | "completed" | "pending" | "stale-owner" | "missing";

const SERVER_CANCEL_TIMEOUT_MS = 5_000;
const ADMIN_CANCEL_WRITER_TTL_MS = 2 * 60 * 1000;

type ExpectedOwner = Pick<FileUploadSession, "uploadOwnerId" | "uploadOwnerClaimId">;

export interface AdminUploadCancellationWriter {
	ownership: DurableOwnershipPort;
	token: DurableLeaseToken;
}

export interface AdminUploadCancellationRequest {
	sessionId: string;
	/** The identity observed by the row that rendered the cancel affordance. */
	expectedUploadId?: string;
	expectedOwner?: ExpectedOwner;
	/** Reconciliation callers only operate on an already durable cancellation tombstone. */
	expectedStatus?: "cancelled";
	/** Reconciliation callers use this to fence server abort and local cleanup to their writer epoch. */
	assertWriterEpoch?: () => Promise<boolean>;
	/** A drain may pass the writer epoch it already acquired for this exact session. */
	writer?: AdminUploadCancellationWriter;
	onTombstoned?: () => void;
}

/**
 * LSP-23105: a tray row captured before the row's first target was attached holds its `pending-target:`
 * placeholder, and attaching rewrites the row's uploadId. That placeholder names the row's first target,
 * so it matches only a row stamped as still on it (`targetReattached === false`). A replacement target
 * (R-1 repoint), or a row attached before the stamp existed, refuses — as does a real-to-real change
 * (LSP-17193). Every CAS after this gate fences on the freshly observed identity, never the placeholder.
 */
function uploadIdMatches(session: FileUploadSession, expectedUploadId?: string): boolean {
	if (expectedUploadId === undefined || session.uploadId === expectedUploadId) return true;
	return isOwnPendingTargetPlaceholder(session.id, expectedUploadId) && session.targetReattached === false;
}

function ownerMatches(session: FileUploadSession, expectedOwner?: ExpectedOwner): boolean {
	if (!expectedOwner) return true;
	return session.uploadOwnerId === expectedOwner.uploadOwnerId && session.uploadOwnerClaimId === expectedOwner.uploadOwnerClaimId;
}

function isAlreadyGone(error: unknown): boolean {
	const responseError = error as { status?: unknown; code?: unknown; httpStatus?: unknown } | null;
	// api._handleResponse preserves a JSON error's semantic code and records the HTTP status
	// separately. `/uploads` therefore rejects a 404 as
	// `{ code: "UPLOAD_NOT_FOUND", httpStatus: 404 }`, not `{ code: 404 }`.
	return responseError?.status === 404 || responseError?.code === 404 || responseError?.httpStatus === 404;
}

/** Abort success also covers completion winning. Read the exact attempt before disposing its bytes. */
async function reconcileCancellation(session: FileUploadSession): Promise<"completed" | "acknowledged" | "pending"> {
	if (session.endpointFamily !== "uploads" && !session.registryId) return "acknowledged";
	if (session.targetPending && !session.targetInitiationStarted) return "acknowledged";
	let receiptTimeout: ReturnType<typeof setTimeout> | undefined;
	try {
		const projection = (await Promise.race([
			api.getUploadsUpload(session.registryId ?? session.uploadId),
			new Promise<never>((_resolve, reject) => {
				receiptTimeout = setTimeout(() => reject(new Error("Cancellation registry read timed out")), SERVER_CANCEL_TIMEOUT_MS);
			})
		])) as {
			id?: unknown;
			status?: unknown;
			attempt?: { id?: unknown; generation?: unknown; takeGeneration?: unknown };
		} | null;
		if (
			!projection ||
			projection.id !== (session.registryId ?? session.uploadId) ||
			projection.attempt?.id !== session.attemptId ||
			projection.attempt?.generation !== session.generation ||
			(session.takeGeneration !== undefined && projection.attempt?.takeGeneration !== session.takeGeneration)
		) {
			return cancellationDidNotTakeEffect("pending", "registry-identity-mismatch", session);
		}
		if (projection.status === "ready" || projection.status === "sealed") return "completed";
		if (projection.status === "cancelled") return "acknowledged";
		return cancellationDidNotTakeEffect("pending", `registry-not-terminal:${String(projection.status)}`, session);
	} catch (error) {
		// A successful abort may soft-delete its target; an unavailable read proves nothing.
		if (isAlreadyGone(error)) return "acknowledged";
		console.warn("[Upload] Cancellation outcome could not be verified; retaining local data", {
			sessionId: session.id,
			registryId: session.registryId,
			...uploadFailureLogDetails(error)
		});
		return "pending";
	} finally {
		clearTimeout(receiptTimeout);
	}
}

function getServerUploadAbortTarget(
	session: FileUploadSession
):
	| { family: "uploads"; registryId: string; attemptId: string; generation: number; takeGeneration?: string }
	| { family: "legacy"; uploadId: string; s3Key: string; bucket: string } {
	if (session.endpointFamily === "uploads" || session.registryId) {
		const generation = session.generation;
		if (
			!session.registryId ||
			!session.attemptId ||
			typeof generation !== "number" ||
			!Number.isSafeInteger(generation) ||
			generation <= 0
		) {
			throw new Error("Uploads cancellation is missing its persisted attempt identity");
		}
		return {
			family: "uploads",
			registryId: session.registryId,
			attemptId: session.attemptId,
			generation,
			takeGeneration: session.takeGeneration
		};
	}
	return { family: "legacy", uploadId: session.uploadId, s3Key: session.s3Key, bucket: session.bucket };
}

/**
 * LSP-22007 (silent-failure rule): these returns leave the row untombstoned and nothing retries them, so
 * a caller that discards the result — post-refresh cleanup did — left no trace of which condition held.
 */
function cancellationDidNotTakeEffect<T extends AdminUploadCancellationResult>(result: T, reason: string, session: FileUploadSession): T {
	console.warn("[Upload] Cancellation did not take effect", {
		sessionId: session.id,
		uploadId: session.uploadId,
		status: session.status,
		result,
		reason
	});
	return result;
}

type CancellationWriter = AdminUploadCancellationWriter & { releaseWhenDone: boolean };

interface CancellationWriterAcquisition {
	ownership?: DurableOwnershipPort;
	writer?: CancellationWriter;
}

async function acquireCancellationWriter(
	session: FileUploadSession,
	request: AdminUploadCancellationRequest,
	existingOwnership?: DurableOwnershipPort
): Promise<CancellationWriterAcquisition> {
	if (request.writer) return { ownership: request.writer.ownership, writer: { ...request.writer, releaseWhenDone: false } };
	let ownership: DurableOwnershipPort | undefined;
	try {
		ownership = existingOwnership ?? (await getAdminDurableOwnershipForCoordinator());
	} catch (error) {
		console.warn(
			"[Upload] Durable cancellation writer ownership initialization failed; cancellation will remain pending when no writer is available",
			{
				sessionId: session.id,
				registryId: session.registryId,
				attemptId: session.attemptId,
				generation: session.generation,
				takeGeneration: session.takeGeneration,
				uploadId: session.uploadId
			},
			error
		);
		return {};
	}
	// The legacy unit harness deliberately disables the coordinator. Production must never run a
	// destructive cancellation without a durable writer epoch; the caller maps this absence to a
	// retryable pending result outside test mode.
	if (!ownership) return {};
	const token = await ownership.acquireWriter({
		originKey: "contact-admin",
		identity: { itemId: session.id },
		ownerId: `admin-cancel:${newUploadId()}`,
		ttlMs: ADMIN_CANCEL_WRITER_TTL_MS
	});
	return { ownership, writer: token ? { ownership, token, releaseWhenDone: true } : undefined };
}

async function releaseCancellationWriter(writer: CancellationWriter, session: FileUploadSession): Promise<void> {
	try {
		await writer.ownership.releaseWriter(writer.token);
	} catch (error) {
		console.warn(
			"[Upload] Failed to release cancellation writer; subsequent cancellation may wait for lease expiry",
			{
				sessionId: session.id,
				registryId: session.registryId,
				attemptId: session.attemptId,
				generation: session.generation,
				takeGeneration: session.takeGeneration,
				uploadId: session.uploadId,
				writerOwnerId: writer.token.ownerId,
				writerEpoch: writer.token.epoch
			},
			error
		);
	}
}

function createCancellationGate(
	writer: CancellationWriter | undefined,
	request: AdminUploadCancellationRequest,
	storeNames: readonly string[]
): OwnershipGate<FileUploadSession> {
	const assertWriter = async () => {
		if (request.assertWriterEpoch && !(await request.assertWriterEpoch())) return false;
		return writer ? writer.ownership.ownsWriter(writer.token) : true;
	};
	return {
		owns: assertWriter,
		async commitIfOwned<T>(_session: FileUploadSession, effect: () => Promise<T>) {
			if (!writer) return (await assertWriter()) ? { kind: "applied", value: await effect() } : { kind: "skipped-not-owned" };
			if (storeNames.length === 0) {
				const check = await writer.ownership.commitIfWriterOwns({ token: writer.token, effect: async () => true });
				if (check.kind === "skipped-not-owned" || !(await assertWriter())) return { kind: "skipped-not-owned" };
				return { kind: "applied", value: await effect() };
			}
			return writer.ownership.commitIfWriterOwns({
				token: writer.token,
				storeNames,
				effect: async () => {
					if (!(await assertWriter())) return undefined as T;
					return effect();
				}
			});
		}
	};
}

/**
 * Records the user discard as a durable terminal intent before allowing local cleanup or
 * cancellation of the in-flight writer. The cancelled row is the retry anchor until the server
 * acknowledges abort/revocation; a later drain can retry it without dispatching another upload.
 */
export async function cancelAdminUploadSession(request: AdminUploadCancellationRequest): Promise<AdminUploadCancellationResult> {
	const initial = await getFileUploadSessionById(request.sessionId);
	if (!initial) return "missing";
	let observed: FileUploadSession = initial;
	if (!uploadIdMatches(observed, request.expectedUploadId))
		return cancellationDidNotTakeEffect("stale-owner", "upload-id-changed", observed);
	if (request.expectedStatus !== undefined && observed.status !== request.expectedStatus)
		return cancellationDidNotTakeEffect("stale-owner", "status-changed", observed);

	// A handed-off row is intentionally ownerless. A non-empty, changed owner means this tab is stale
	// and must yield; it must never abort or delete a successor's target.
	if (request.expectedOwner && !ownerMatches(observed, request.expectedOwner) && observed.uploadOwnerId !== undefined) {
		return cancellationDidNotTakeEffect("stale-owner", "owner-changed", observed);
	}
	let acquisition = await acquireCancellationWriter(observed, request);
	let writer = acquisition.writer;
	let handedOffExistingWriter = false;
	// Completion has already won; a still-live completing writer owns its remaining cleanup.
	if (!writer && acquisition.ownership && observed.status === "complete") return "completed";
	if (!writer && acquisition.ownership && observed.status !== "cancelled" && !request.writer) {
		const handoffOutcome = await handoffFileUploadSessionToCancellation(observed.id, {
			status: observed.status,
			uploadId: observed.uploadId,
			uploadOwnerId: observed.uploadOwnerId,
			uploadOwnerClaimId: observed.uploadOwnerClaimId,
			endpointFamily: observed.endpointFamily,
			registryId: observed.registryId,
			attemptId: observed.attemptId,
			generation: observed.generation,
			takeGeneration: observed.takeGeneration,
			s3Key: observed.s3Key,
			bucket: observed.bucket
		});
		if (handoffOutcome === "missing") return "missing";
		if (handoffOutcome === "stale-owner") return cancellationDidNotTakeEffect("stale-owner", "handoff-identity-changed", observed);
		handedOffExistingWriter = handoffOutcome === "tombstoned";
		if (handedOffExistingWriter) request.onTombstoned?.();
		const handedOffSession = await getFileUploadSessionById(request.sessionId);
		if (!handedOffSession) return "missing";
		observed = handedOffSession;
		acquisition = await acquireCancellationWriter(observed, request, acquisition.ownership);
		writer = acquisition.writer;
	}
	if (!writer && acquisition.ownership)
		return cancellationDidNotTakeEffect(
			"pending",
			handedOffExistingWriter ? "writer-taken-after-handoff" : "writer-unavailable",
			observed
		);
	// TRANSITIONAL — this MODE sniff is the same seam defect as adminUploadLease.getDurableOwnership:
	// the code has no typed way to say "durable ownership is unavailable", so it asks "is this a test
	// build?" instead of asking a capability question, and lets cancellation proceed without a writer
	// in test builds. It is not intended design and not a sanctioned production fallback; it is not
	// removable today (removing the fork family was measured at 254 failing tests). Scheduled for
	// deletion by the registered "admin upload ownership seam" follow-up.
	if (!writer && import.meta.env?.MODE !== "test" && !request.writer)
		return cancellationDidNotTakeEffect("pending", "no-durable-ownership", observed);

	// Writer acquisition can wait behind another cancellation. Re-read before any remote effect so
	// a winner that already deleted the exact row cannot be followed by a duplicate abort.
	if (writer) {
		const current = await getFileUploadSessionById(request.sessionId);
		if (!current) {
			if (writer.releaseWhenDone) await releaseCancellationWriter(writer, observed);
			return "missing";
		}
		if (!uploadIdMatches(current, request.expectedUploadId)) {
			if (writer.releaseWhenDone) await releaseCancellationWriter(writer, current);
			return cancellationDidNotTakeEffect("stale-owner", "upload-id-changed-under-writer", current);
		}
		if (request.expectedStatus !== undefined && current.status !== request.expectedStatus) {
			if (writer.releaseWhenDone) await releaseCancellationWriter(writer, current);
			return cancellationDidNotTakeEffect("stale-owner", "status-changed-under-writer", current);
		}
		if (
			!handedOffExistingWriter &&
			request.expectedOwner &&
			!ownerMatches(current, request.expectedOwner) &&
			current.uploadOwnerId !== undefined
		) {
			if (writer.releaseWhenDone) await releaseCancellationWriter(writer, current);
			return cancellationDidNotTakeEffect("stale-owner", "owner-changed-under-writer", current);
		}
		observed = current;
	}

	const expectedIdentity = {
		uploadId: observed.uploadId,
		uploadOwnerId: observed.uploadOwnerId,
		uploadOwnerClaimId: observed.uploadOwnerClaimId
	};
	try {
		if (observed.status === "complete") {
			// Completion is already durable. Leftover cleanup must not turn it back into pending cancellation.
			try {
				const outcome = await runGuardedEffect(
					createCancellationGate(writer, request, [
						"fileUploadSessions",
						"largeFileUploadSessions",
						"fileUploadChunks",
						"fileUploadChunkData"
					]),
					observed,
					async () =>
						(await cleanupCompletedFileUploadSession(
							observed.id,
							observed.uploadId,
							observed.uploadOwnerId,
							observed.uploadOwnerClaimId
						))
							? "completed"
							: "pending"
				);
				if (outcome !== "completed") {
					console.warn("[Upload] Completed upload cleanup deferred; preserving completion", {
						sessionId: observed.id,
						outcome
					});
				}
			} catch (error) {
				console.warn("[Upload] Completed upload cleanup failed; preserving completion", {
					sessionId: observed.id,
					...uploadFailureLogDetails(error)
				});
			}
			return "completed";
		}
		if (observed.status !== "cancelled") {
			const tombstoneOutcome = await runGuardedEffect(
				createCancellationGate(writer, request, [
					"fileUploadSessions",
					"largeFileUploadSessions",
					"fileUploadChunks",
					"fileUploadChunkData"
				]),
				observed,
				async () =>
					(await updateFileUploadSessionStatus(observed.id, "cancelled", expectedIdentity, {
						// The tombstone must commit before transport abort and before any local payload deletion.
						retainChunks: true,
						notifyOtherTabs: true
					}))
						? "tombstoned"
						: "stale-owner"
			);
			if (tombstoneOutcome === "skipped-not-owned")
				return cancellationDidNotTakeEffect("stale-owner", "tombstone-writer-lost", observed);
			// A declined CAS is usually completion winning (terminal precedence) or a successor claim.
			if (tombstoneOutcome !== "tombstoned") return cancellationDidNotTakeEffect("stale-owner", "tombstone-declined", observed);
			if (!handedOffExistingWriter) request.onTombstoned?.();
		}

		const abortOutcome = await runGuardedEffect(createCancellationGate(writer, request, []), observed, async () => {
			try {
				// A deferred source with no initiation attempt has nothing to abort remotely.
				if (observed.targetPending && !observed.targetInitiationStarted) return "acknowledged" as const;
				let abortSession = observed;
				if (observed.targetPending) {
					// Reissue the same idempotent create after an ambiguous response. This runs under
					// the cancellation writer, never attaches/requeues the cancelled source, and lets
					// the durable cancellation drain retry when the API is unavailable.
					const target = await initiateAdminPersistedTarget({
						session: observed,
						stableId: observed.id,
						totalChunks: observed.totalChunks
					});
					if (!isUploadsUploadTarget(target)) throw new Error("Deferred Admin target returned a different endpoint family");
					abortSession = {
						...observed,
						registryId: target.registryId,
						attemptId: target.attemptId,
						generation: target.generation,
						takeGeneration: target.takeGeneration
					};
					if (request.assertWriterEpoch && !(await request.assertWriterEpoch())) return "writer-lost" as const;
					if (writer && !(await writer.ownership.ownsWriter(writer.token))) return "writer-lost" as const;
					// Keep the recovered identity before aborting: a lost abort response must retry
					// that abort, not create another server attempt for the cancelled source.
					const checkpoint = await runGuardedEffect(
						createCancellationGate(writer, request, ["fileUploadSessions", "largeFileUploadSessions"]),
						observed,
						async () => {
							await updateFileUploadSession(observed.id, {
								registryId: target.registryId,
								attemptId: target.attemptId,
								generation: target.generation,
								takeGeneration: target.takeGeneration,
								targetPending: false
							});
							return "checkpointed" as const;
						}
					);
					if (checkpoint === "skipped-not-owned") return "writer-lost" as const;
					observed = { ...abortSession, targetPending: false };
				}
				const abortTarget = getServerUploadAbortTarget(abortSession);
				const abortRequest =
					abortTarget.family === "uploads"
						? abortTarget.takeGeneration
							? api.abortUploadsUpload(
									abortTarget.registryId,
									abortTarget.attemptId,
									abortTarget.generation,
									"multipart",
									abortTarget.takeGeneration
								)
							: api.abortUploadsUpload(abortTarget.registryId, abortTarget.attemptId, abortTarget.generation, "multipart")
						: api.abortMultipartUpload(abortTarget.uploadId, abortTarget.s3Key, abortTarget.bucket, true);
				const outcome = await Promise.race([
					abortRequest.then(() => "acknowledged" as const),
					new Promise<"timed-out">((resolve) => setTimeout(() => resolve("timed-out"), SERVER_CANCEL_TIMEOUT_MS))
				]);
				if (outcome === "timed-out") {
					void abortRequest.catch((error) =>
						console.warn(
							"[Upload] Server abort failed after cancellation timed out; cancellation remains pending",
							{
								sessionId: observed.id,
								registryId: observed.registryId,
								attemptId: observed.attemptId,
								generation: observed.generation,
								takeGeneration: observed.takeGeneration,
								uploadId: observed.uploadId,
								timedOut: true
							},
							error
						)
					);
					return "pending" as const;
				}
				return "acknowledged" as const;
			} catch (error) {
				if (isAlreadyGone(error)) return "acknowledged" as const;
				console.warn(
					"[Upload] Server abort failed immediately; cancellation remains pending",
					{
						sessionId: observed.id,
						registryId: observed.registryId,
						attemptId: observed.attemptId,
						generation: observed.generation,
						takeGeneration: observed.takeGeneration,
						uploadId: observed.uploadId
					},
					error
				);
				return "pending" as const;
			}
		});
		// The abort/initiate failures already log where they happen; a lost writer epoch did not.
		if (abortOutcome === "skipped-not-owned" || abortOutcome === "writer-lost")
			return cancellationDidNotTakeEffect("pending", "abort-writer-lost", observed);
		if (abortOutcome === "pending") return "pending";
		const reconciled = await reconcileCancellation(observed);
		if (reconciled === "pending") return "pending";

		// Only the cancelled row whose immutable upload identity we observed may be removed. This is
		// completion-wins safe: the server's successful terminal response is the acknowledgement.
		const deletedOutcome = await runGuardedEffect(
			// captureEvidenceManifests belongs here because deleteFileUploadSession opens its own
			// sub-transaction over it; a table the child needs but this parent omits makes Dexie throw
			// SubTransactionError rather than silently skipping the manifest cleanup.
			createCancellationGate(writer, request, [
				"fileUploadSessions",
				"largeFileUploadSessions",
				"fileUploadChunks",
				"fileUploadChunkData",
				"captureEvidenceManifests"
			]),
			observed,
			async () => {
				const current = await getFileUploadSessionById(observed.id);
				if (
					!current ||
					current.uploadId !== observed.uploadId ||
					(reconciled === "completed" && current.status !== "cancelled") ||
					current.registryId !== observed.registryId ||
					current.attemptId !== observed.attemptId ||
					current.generation !== observed.generation ||
					current.takeGeneration !== observed.takeGeneration
				) {
					return cancellationDidNotTakeEffect("pending", "cleanup-target-changed", observed);
				}
				if (reconciled === "completed") {
					// The exact server receipt outranks the local cancellation intent. Commit completion
					// and payload cleanup atomically, preserving the normal completed batch history.
					await updateFileUploadSession(observed.id, { status: "complete", completedAt: current.completedAt ?? Date.now() });
					const cleaned = await cleanupCompletedFileUploadSession(
						observed.id,
						observed.uploadId,
						current.uploadOwnerId,
						current.uploadOwnerClaimId
					);
					if (!cleaned) throw new Error("Reconciled completion cleanup lost its local session identity");
					return "deleted";
				}
				return (await deleteFileUploadSession(observed.id, observed.uploadId, undefined, undefined, {
					expectedStatus: "cancelled",
					notifyOtherTabs: false,
					expectedWriterLease: writer?.token
				}))
					? "deleted"
					: "pending";
			}
		);
		if (deletedOutcome === "skipped-not-owned") return cancellationDidNotTakeEffect("pending", "delete-writer-lost", observed);
		return deletedOutcome === "deleted" ? reconciled : "pending";
	} finally {
		if (writer?.releaseWhenDone) await releaseCancellationWriter(writer, observed);
	}
}
